import { useNavigate } from 'react-router';
import { ENUM_LABELS, type LabTest, type TestParameter } from '#shared/types';
import { Badge } from '#client/components/ui/Badge';
import { Card } from '#client/components/ui/Card';
import { type Column, DataTable } from '#client/components/ui/DataTable';
import { DetailList } from '#client/components/ui/DetailList';
import { ResourceDetailPage } from '#client/components/ui/ResourceDetailPage';
import { formatDateTime, formatMoney, formatTat, dash } from '#client/lib/format';
import { RESOURCE, routes } from '#client/lib/routes';

const PATH = RESOURCE.labTests;

const parameterColumns: Column<TestParameter>[] = [
  { key: 'name', header: 'Parameter', render: (row) => <strong>{row.name}</strong> },
  { key: 'code', header: 'Code', render: (row) => row.code },
  {
    key: 'ranges',
    header: 'Ranges',
    render: (row) => <Badge tone="neutral">{row.values.length}</Badge>,
    align: 'center',
  },
];

export function LabTestDetailPage() {
  const navigate = useNavigate();

  return (
    <ResourceDetailPage<LabTest>
      path={PATH}
      noun="lab test"
      editCapability="labTest.edit"
      deleteCapability="labTest.delete"
      title={(test) => test.name}
      subtitle={(test) => `Lab test ${test.code}`}
      deleteLabel={(test) => test.name}
    >
      {(test) => (
        <>
          <Card title="Details">
            <DetailList
              items={[
                { label: 'Name', value: test.name },
                { label: 'Code', value: test.code },
                { label: 'Report type', value: ENUM_LABELS.reportKind[test.report_kind] },
                { label: 'Price', value: formatMoney(test.price) },
                { label: 'Turnaround', value: formatTat(test.turnaround_hours) },
                { label: 'Created', value: formatDateTime(test.created_at) },
                { label: 'Comment', value: dash(test.comment), wide: true },
                {
                  label: 'Sample instructions',
                  value: dash(test.sample_instructions),
                  wide: true,
                },
              ]}
            />
          </Card>

          {test.report_kind === 'culture' ? (
            <Card title={`Antibiotic panel (${test.antibiotics.length})`}>
              <p>
                {test.antibiotics.map((antibiotic) => antibiotic.name).join(', ') ||
                  'No antibiotics on the panel'}
              </p>
            </Card>
          ) : (
            <Card title={`Parameters (${test.parameters.length})`} flush>
              <DataTable
                columns={parameterColumns}
                rows={test.parameters}
                rowKey={(row) => row.id}
                caption={`Parameters in ${test.name}`}
                emptyState="No parameters attached"
                onRowClick={(row) => void navigate(routes.testParameter(row.id))}
              />
            </Card>
          )}
        </>
      )}
    </ResourceDetailPage>
  );
}
